// src/constants.ts
var ALLOWED_ORIGINS = [
  "http://localhost:5173",
  "http://localhost:3000",
  "https://sapiens-iot.com",
  "https://sein.sapiens-iot.com",
  "https://sso.sapiens-iot.com",
  "https://www.sapiens-iot.com",
  "https://sein-admin.sapiens-iot.com"
];
var DEFAULT_STORAGE_PREFIX = "sapiens_auth";
var STORAGE_KEYS = {
  TOKEN: "token",
  EXPIRES_AT: "expires_at",
  USER: "user",
  CREDENTIALS: "credentials",
  CREDENTIALS_IV: "credentials_iv"
};
var DEFAULT_POPUP_TIMEOUT = 12e4;
var DEFAULT_POPUP_WIDTH = 500;
var DEFAULT_POPUP_HEIGHT = 600;
var TOKEN_EXPIRY_BUFFER = 6e4;

// src/tokenStorage.ts
var TokenStorage = class {
  storage;
  prefix;
  constructor(type = "localStorage", prefix = DEFAULT_STORAGE_PREFIX) {
    this.prefix = prefix;
    switch (type) {
      case "localStorage":
        this.storage = window.localStorage;
        break;
      case "sessionStorage":
        this.storage = window.sessionStorage;
        break;
      case "memory":
        this.storage = /* @__PURE__ */ new Map();
        break;
    }
  }
  key(name) {
    return `${this.prefix}_${name}`;
  }
  getItem(key) {
    if (this.storage instanceof Map) {
      return this.storage.get(key) ?? null;
    }
    return this.storage.getItem(key);
  }
  setItem(key, value) {
    if (this.storage instanceof Map) {
      this.storage.set(key, value);
    } else {
      this.storage.setItem(key, value);
    }
  }
  removeItem(key) {
    if (this.storage instanceof Map) {
      this.storage.delete(key);
    } else {
      this.storage.removeItem(key);
    }
  }
  /**
   * Store token with expiration time
   */
  setToken(token, expiresAt) {
    this.setItem(this.key(STORAGE_KEYS.TOKEN), token);
    this.setItem(this.key(STORAGE_KEYS.EXPIRES_AT), String(expiresAt));
  }
  /**
   * Get token if not expired
   */
  getToken() {
    const expiresAt = this.getExpiresAt();
    if (expiresAt && Date.now() > expiresAt) {
      this.clearToken();
      return null;
    }
    return this.getItem(this.key(STORAGE_KEYS.TOKEN));
  }
  /**
   * Get token expiration timestamp
   */
  getExpiresAt() {
    const value = this.getItem(this.key(STORAGE_KEYS.EXPIRES_AT));
    return value ? parseInt(value, 10) : null;
  }
  /**
   * Check if token is expired or will expire within buffer time
   */
  isTokenExpired(bufferMs = 0) {
    const expiresAt = this.getExpiresAt();
    if (!expiresAt) return true;
    return Date.now() + bufferMs >= expiresAt;
  }
  /**
   * Clear token and expiration
   */
  clearToken() {
    this.removeItem(this.key(STORAGE_KEYS.TOKEN));
    this.removeItem(this.key(STORAGE_KEYS.EXPIRES_AT));
  }
  /**
   * Store user info
   */
  setUser(user) {
    this.setItem(this.key(STORAGE_KEYS.USER), JSON.stringify(user));
  }
  /**
   * Get stored user info
   */
  getUser() {
    const value = this.getItem(this.key(STORAGE_KEYS.USER));
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  /**
   * Clear user info
   */
  clearUser() {
    this.removeItem(this.key(STORAGE_KEYS.USER));
  }
  /**
   * Clear all auth data
   */
  clear() {
    this.clearToken();
    this.clearUser();
  }
};

// src/credentialStorage.ts
var CredentialStorage = class {
  prefix;
  keyPromise = null;
  constructor(prefix = DEFAULT_STORAGE_PREFIX) {
    this.prefix = prefix;
  }
  key(name) {
    return `${this.prefix}_${name}`;
  }
  /**
   * Get or derive encryption key from device fingerprint
   * Uses a combination of user agent and origin as key material
   */
  async getEncryptionKey() {
    if (this.keyPromise) {
      return this.keyPromise;
    }
    this.keyPromise = (async () => {
      const keyMaterial = `${window.navigator.userAgent}::${window.location.origin}::sapiens-auth-key`;
      const encoder = new TextEncoder();
      const keyData = encoder.encode(keyMaterial);
      const hashBuffer = await crypto.subtle.digest("SHA-256", keyData);
      return crypto.subtle.importKey(
        "raw",
        hashBuffer,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );
    })();
    return this.keyPromise;
  }
  /**
   * Encrypt credentials using AES-GCM
   */
  async encrypt(data) {
    const key = await this.getEncryptionKey();
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      dataBuffer
    );
    const encrypted = btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)));
    const ivString = btoa(String.fromCharCode(...iv));
    return { encrypted, iv: ivString };
  }
  /**
   * Decrypt credentials using AES-GCM
   */
  async decrypt(encrypted, ivString) {
    const key = await this.getEncryptionKey();
    const encryptedBuffer = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(ivString), (c) => c.charCodeAt(0));
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encryptedBuffer
    );
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  }
  /**
   * Save encrypted credentials
   */
  async saveCredentials(credentials) {
    try {
      const data = JSON.stringify(credentials);
      const { encrypted, iv } = await this.encrypt(data);
      localStorage.setItem(this.key(STORAGE_KEYS.CREDENTIALS), encrypted);
      localStorage.setItem(this.key(STORAGE_KEYS.CREDENTIALS_IV), iv);
    } catch (error) {
      console.error("Failed to save credentials:", error);
      throw new Error("Failed to save credentials");
    }
  }
  /**
   * Load and decrypt credentials
   */
  async getCredentials() {
    try {
      const encrypted = localStorage.getItem(this.key(STORAGE_KEYS.CREDENTIALS));
      const iv = localStorage.getItem(this.key(STORAGE_KEYS.CREDENTIALS_IV));
      if (!encrypted || !iv) {
        return null;
      }
      const data = await this.decrypt(encrypted, iv);
      return JSON.parse(data);
    } catch (error) {
      console.error("Failed to load credentials:", error);
      this.clearCredentials();
      return null;
    }
  }
  /**
   * Check if credentials are stored
   */
  hasCredentials() {
    return localStorage.getItem(this.key(STORAGE_KEYS.CREDENTIALS)) !== null && localStorage.getItem(this.key(STORAGE_KEYS.CREDENTIALS_IV)) !== null;
  }
  /**
   * Clear stored credentials
   */
  clearCredentials() {
    localStorage.removeItem(this.key(STORAGE_KEYS.CREDENTIALS));
    localStorage.removeItem(this.key(STORAGE_KEYS.CREDENTIALS_IV));
  }
};

// src/eventEmitter.ts
var EventEmitter = class {
  listeners = /* @__PURE__ */ new Map();
  /**
   * Subscribe to an event
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, /* @__PURE__ */ new Set());
    }
    this.listeners.get(event).add(callback);
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }
  /**
   * Emit an event to all subscribers
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error("Error in event listener:", error);
        }
      });
    }
  }
  /**
   * Remove all listeners for an event
   */
  off(event) {
    this.listeners.delete(event);
  }
  /**
   * Remove all listeners
   */
  clear() {
    this.listeners.clear();
  }
};
var AUTH_STATE_CHANGE_EVENT = "authStateChange";

// src/client.ts
var SapiensAuthClient = class {
  config;
  tokenStorage;
  credentialStorage;
  eventEmitter;
  autoLoginInProgress = false;
  tokenExpiryTimer = null;
  constructor(config) {
    this.config = {
      storage: "localStorage",
      storageKeyPrefix: DEFAULT_STORAGE_PREFIX,
      popupTimeout: DEFAULT_POPUP_TIMEOUT,
      ...config
    };
    this.tokenStorage = new TokenStorage(
      this.config.storage,
      this.config.storageKeyPrefix
    );
    this.credentialStorage = new CredentialStorage(this.config.storageKeyPrefix);
    this.eventEmitter = new EventEmitter();
    this.setupTokenExpiryMonitor();
  }
  /**
   * Monitor token expiration and trigger auto-login if credentials are stored
   */
  setupTokenExpiryMonitor() {
    const checkExpiry = () => {
      if (this.tokenExpiryTimer) {
        clearTimeout(this.tokenExpiryTimer);
      }
      const expiresAt = this.tokenStorage.getExpiresAt();
      if (!expiresAt) return;
      const timeUntilExpiry = expiresAt - Date.now() - TOKEN_EXPIRY_BUFFER;
      if (timeUntilExpiry <= 0) {
        this.handleTokenExpiry();
      } else {
        this.tokenExpiryTimer = setTimeout(() => {
          this.handleTokenExpiry();
        }, timeUntilExpiry);
      }
    };
    checkExpiry();
  }
  /**
   * Handle token expiration - attempt auto-login if credentials stored
   */
  async handleTokenExpiry() {
    if (this.autoLoginInProgress) return;
    if (this.credentialStorage.hasCredentials()) {
      await this.autoLogin();
    } else {
      this.tokenStorage.clear();
      this.emitAuthStateChange(null);
    }
  }
  /**
   * Attempt automatic login with stored credentials
   */
  async autoLogin() {
    if (this.autoLoginInProgress) return null;
    const credentials = await this.credentialStorage.getCredentials();
    if (!credentials) return null;
    this.autoLoginInProgress = true;
    this.config.onAutoLoginStart?.();
    try {
      const result = await this.login({
        email: credentials.email,
        password: credentials.password,
        rememberMe: true
        // Keep credentials stored
      });
      this.config.onAutoLoginSuccess?.(result.user);
      return result;
    } catch (error) {
      this.config.onAutoLoginError?.(error);
      this.credentialStorage.clearCredentials();
      this.tokenStorage.clear();
      this.emitAuthStateChange(null);
      return null;
    } finally {
      this.autoLoginInProgress = false;
    }
  }
  /**
   * Login with email and password
   */
  async login(credentials) {
    const response = await fetch(`${this.config.apiBaseUrl}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
        credentialType: "password"
      })
    });
    const result = await response.json();
    if (!response.ok || !result.success || !result.data) {
      throw new Error(result.message || "Login failed");
    }
    const { accessToken, expiresIn, user } = result.data;
    const expiresAt = Date.now() + expiresIn * 1e3;
    this.tokenStorage.setToken(accessToken, expiresAt);
    this.tokenStorage.setUser(user);
    if (credentials.rememberMe) {
      await this.credentialStorage.saveCredentials({
        email: credentials.email,
        password: credentials.password
      });
    } else {
      this.credentialStorage.clearCredentials();
    }
    this.setupTokenExpiryMonitor();
    this.emitAuthStateChange(user);
    return { token: accessToken, expiresAt, user };
  }
  /**
   * Login via popup window (for SSO from external platforms)
   */
  loginWithPopup(options) {
    return new Promise((resolve, reject) => {
      const {
        targetOrigin,
        width = DEFAULT_POPUP_WIDTH,
        height = DEFAULT_POPUP_HEIGHT
      } = options;
      const state = crypto.randomUUID();
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        `${this.config.ssoLoginUrl}?targetOrigin=${encodeURIComponent(targetOrigin)}&state=${state}`,
        "sapiensSSOPopup",
        `width=${width},height=${height},left=${left},top=${top},popup`
      );
      if (!popup) {
        reject(new Error("Popup was blocked. Please allow popups for this site."));
        return;
      }
      let resolved = false;
      const cleanup = () => {
        window.removeEventListener("message", messageHandler);
        clearInterval(pollTimer);
        clearTimeout(timeoutTimer);
      };
      const messageHandler = (event) => {
        if (!ALLOWED_ORIGINS.includes(event.origin)) return;
        if (event.data?.type === "SSO_SUCCESS" && event.data?.state === state) {
          resolved = true;
          cleanup();
          popup.close();
          if (event.data.token && event.data.user) {
            const expiresAt = event.data.expiresAt || Date.now() + 36e5;
            this.tokenStorage.setToken(event.data.token, expiresAt);
            this.tokenStorage.setUser(event.data.user);
            this.setupTokenExpiryMonitor();
            this.emitAuthStateChange(event.data.user);
            resolve({
              token: event.data.token,
              expiresAt,
              user: event.data.user
            });
          } else {
            reject(new Error("Invalid SSO response"));
          }
        } else if (event.data?.type === "SSO_ERROR" && event.data?.state === state) {
          resolved = true;
          cleanup();
          popup.close();
          reject(new Error(event.data.error || "Authentication failed"));
        } else if (event.data?.type === "SSO_CANCEL" && event.data?.state === state) {
          resolved = true;
          cleanup();
          popup.close();
          reject(new Error("Authentication cancelled"));
        }
      };
      window.addEventListener("message", messageHandler);
      const pollTimer = setInterval(() => {
        if (popup.closed && !resolved) {
          cleanup();
          reject(new Error("Authentication cancelled"));
        }
      }, 500);
      const timeoutTimer = setTimeout(() => {
        if (!resolved) {
          cleanup();
          popup.close();
          reject(new Error("Authentication timed out"));
        }
      }, this.config.popupTimeout);
    });
  }
  /**
   * Logout - clear all auth data
   */
  logout() {
    if (this.tokenExpiryTimer) {
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
    this.tokenStorage.clear();
    this.credentialStorage.clearCredentials();
    this.emitAuthStateChange(null);
  }
  /**
   * Get current token if valid
   */
  getToken() {
    return this.tokenStorage.getToken();
  }
  /**
   * Get current user info
   */
  getUser() {
    if (this.tokenStorage.isTokenExpired()) {
      return null;
    }
    return this.tokenStorage.getUser();
  }
  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.getToken() !== null;
  }
  /**
   * Check if token is about to expire (within buffer)
   */
  isTokenExpiringSoon() {
    return this.tokenStorage.isTokenExpired(TOKEN_EXPIRY_BUFFER);
  }
  /**
   * Check if credentials are stored for auto-login
   */
  hasStoredCredentials() {
    return this.credentialStorage.hasCredentials();
  }
  /**
   * Save credentials for auto-login
   */
  async saveCredentials(email, password) {
    await this.credentialStorage.saveCredentials({ email, password });
  }
  /**
   * Clear stored credentials
   */
  clearCredentials() {
    this.credentialStorage.clearCredentials();
  }
  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback) {
    return this.eventEmitter.on(AUTH_STATE_CHANGE_EVENT, callback);
  }
  /**
   * Emit auth state change event
   */
  emitAuthStateChange(user) {
    this.eventEmitter.emit(AUTH_STATE_CHANGE_EVENT, user);
  }
  /**
   * Validate if an origin is allowed for SSO
   */
  static isOriginAllowed(origin) {
    return ALLOWED_ORIGINS.includes(origin);
  }
  /**
   * Send SSO success message to opener window
   * (Used by SSO login page)
   */
  static sendSSOSuccess(token, user, expiresAt, state, targetOrigin) {
    if (!window.opener) {
      console.error("No opener window found");
      return;
    }
    if (!ALLOWED_ORIGINS.includes(targetOrigin)) {
      console.error("Target origin not allowed:", targetOrigin);
      return;
    }
    const message = {
      type: "SSO_SUCCESS",
      token,
      user,
      expiresAt,
      state
    };
    window.opener.postMessage(message, targetOrigin);
    window.close();
  }
  /**
   * Send SSO error message to opener window
   * (Used by SSO login page)
   */
  static sendSSOError(error, state, targetOrigin) {
    if (!window.opener) {
      console.error("No opener window found");
      return;
    }
    if (!ALLOWED_ORIGINS.includes(targetOrigin)) {
      console.error("Target origin not allowed:", targetOrigin);
      return;
    }
    const message = {
      type: "SSO_ERROR",
      error,
      state
    };
    window.opener.postMessage(message, targetOrigin);
    window.close();
  }
  /**
   * Send SSO cancel message to opener window
   * (Used by SSO login page)
   */
  static sendSSOCancel(state, targetOrigin) {
    if (!window.opener) {
      console.error("No opener window found");
      return;
    }
    if (!ALLOWED_ORIGINS.includes(targetOrigin)) {
      console.error("Target origin not allowed:", targetOrigin);
      return;
    }
    const message = {
      type: "SSO_CANCEL",
      state
    };
    window.opener.postMessage(message, targetOrigin);
    window.close();
  }
};

export { ALLOWED_ORIGINS, AUTH_STATE_CHANGE_EVENT, CredentialStorage, DEFAULT_POPUP_HEIGHT, DEFAULT_POPUP_TIMEOUT, DEFAULT_POPUP_WIDTH, DEFAULT_STORAGE_PREFIX, EventEmitter, STORAGE_KEYS, SapiensAuthClient, TOKEN_EXPIRY_BUFFER, TokenStorage };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map